import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import type { RoleSummary } from './LeafConfigCard';

export type StreamDefinitionLabelMaps = Record<
  string,
  { products: Record<string, string>; teams: Record<string, string>; tenants: Record<string, string> }
>;

function productIds(role: RoleSummary): string[] {
  if (role.products?.length) return role.products;
  if (role.product) return [role.product];
  return [];
}

function teamIds(role: RoleSummary): string[] {
  if (role.teams?.length) return role.teams;
  if (role.teamType) return [role.teamType];
  return [];
}

function tenantIds(role: RoleSummary): string[] {
  if (role.tenants?.length) return role.tenants;
  if (role.tenantCode) return [role.tenantCode];
  return [];
}

interface RoleScopeChipsProps {
  role: RoleSummary;
  definitionLabelMaps?: StreamDefinitionLabelMaps;
  /** 'grid' slightly larger badges; 'hierarchy' compact for 240px cards */
  density?: 'grid' | 'hierarchy';
  className?: string;
}

export function RoleScopeChips({ role, definitionLabelMaps, density = 'grid', className }: RoleScopeChipsProps) {
  const maps = definitionLabelMaps?.[role.streamId];
  const badgeClass =
    density === 'hierarchy' ? 'text-[10px] px-1.5 py-0' : 'text-[10px] px-2 py-0.5';
  const labelClass = 'text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide';

  const prods = productIds(role);
  const tms = teamIds(role);
  const tns = tenantIds(role);

  return (
    <div className={cn('flex flex-col gap-1.5 min-w-0', className)}>
      <div className="flex flex-wrap items-center gap-1">
        <span className={labelClass}>Stream</span>
        <Badge
          variant="outline"
          className={cn(badgeClass, 'font-mono border-dashed')}
          style={
            role.streamColor
              ? {
                  borderColor: role.streamColor,
                  color: role.streamColor,
                }
              : undefined
          }
        >
          {role.streamId}
        </Badge>
      </div>
      {prods.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" aria-label="Products">
          <span className={labelClass}>Products</span>
          {prods.map((id) => (
            <Badge key={id} variant="secondary" className={cn(badgeClass, 'font-mono')}>
              {maps?.products[id] || id}
            </Badge>
          ))}
        </div>
      )}
      {tms.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" aria-label="Teams">
          <span className={labelClass}>Teams</span>
          {tms.map((id) => (
            <Badge key={id} variant="outline" className={cn(badgeClass, 'font-mono')}>
              {maps?.teams[id] || id}
            </Badge>
          ))}
        </div>
      )}
      {tns.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" aria-label="Tenants">
          <span className={labelClass}>Tenants</span>
          {tns.map((id) => (
            <Badge key={id} variant="outline" className={cn(badgeClass)}>
              {maps?.tenants[id] || id}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
